import { MakeGenerics, Route } from "@tanstack/react-location";

import { ExchangeMode, ExchangeOrderType } from "../stores";
import { featureIsEnabled } from "../utils/features.utils";
import Home from "./Home";
import { LiquidityContextProvider } from "./Liquidity/context";

export const paths = {
  root: "/",
  exchange: "/exchange",
  liquidity: {
    root: "/liquidity",
    children: {
      provide: "/provide",
      withdraw: "/withdraw",
      ...(featureIsEnabled("zaps") ? { zap: "/zap" } : {}),
      createPool: "/create-pool",
    },
  },
  orders: "/orders",
};

export const externalRoutes = {
  about: "https://sundaeswap.finance/",
  disclaimer: "https://sundaeswap.finance/terms",
  discord: "https://discord.gg/Sundae",
  explorer: {
    mainnet: {
      cardanoscan: "https://cardanoscan.io",
      cexplorer: "https://cexplorer.io",
    },
    preview: {
      cardanoscan: "https://preview.cardanoscan.io",
      cexplorer: "https://preview.cexplorer.io",
    },
  },
  faq: "https://help.sundaeswap.finance/en/",
  faqFarms: "https://help.sundaeswap.finance/en/collections/3299695-yield-farming",
  feesHelpArticle:
    "https://help.sundaeswap.finance/en/articles/7338192-minimum-ada-available-balance",
  governance: "https://governance.sundaeswap.finance/",
  hotjar: "https://www.hotjar.com/",
  learnMoreAboutWallets: "https://help.sundaeswap.finance/en/collections/3303291-wallets",
  medium: "https://sundaeswap-finance.medium.com/",
  privacy: "https://sundaeswap.finance/privacy",
  terms: "https://sundaeswap.finance/terms",
  risksAndRewards:
    "https://help.sundaeswap.finance/en/articles/5887307-what-are-the-risks-and-rewards",
  twitter: "https://twitter.com/SundaeSwap",
  liquidity: "https://help.sundaeswap.finance/en/collections/3281728-liquidity",
  cardanoTokenRegistryHelp:
    "https://help.sundaeswap.finance/en/articles/7336428-the-cardano-token-registry",
  cryptoTaxCalculatorManual: "https://app.cryptotaxcalculator.io/imports/custom/manual",
  cryptoTaxCalculatorAssistance: "https://cryptotaxcalculator.io/guides/advanced-manual-csv-import",
};

export enum PortfolioTab {
  assets = "assets",
  liquidity = "liquidity",
  yieldFarming = "yieldFarming",
}

export type TLocationGenerics = MakeGenerics<{
  LoaderData: {};
  Search: {
    activeAssetA?: string;
    activeAssetB?: string;
    mode?: ExchangeMode;
    order?: ExchangeOrderType;
    tab?: PortfolioTab;
    zapAssetId?: string;
    zapPoolId?: string;
  };
}>;

export const AppRoutes: Route[] = [
  {
    path: paths.root,
    element: <Home />,
  },
  {
    path: paths.exchange,
    element: () => import("./Exchange/components/ExchangeWrapper").then((m) => <m.default />),
  },
  {
    path: paths.liquidity.root,
    children: [
      {
        path: "/",
        element: () => import("./Liquidity").then((m) => <m.default />),
      },
      {
        path: paths.liquidity.children.createPool,
        element: () =>
          import("./CreatePool/components/CreatePoolWrapper").then((m) => <m.default />),
      },
      ...(featureIsEnabled("zaps")
        ? [
            {
              path: paths.liquidity.children.zap,
              element: () => import("./Zap").then((m) => <m.default />),
            },
          ]
        : []),
      {
        path: paths.liquidity.children.withdraw,
        element: () =>
          import("./WithdrawLiquidity").then((m) => (
            <LiquidityContextProvider>
              <m.default />
            </LiquidityContextProvider>
          )),
      },
      {
        path: paths.liquidity.children.provide,
        element: () =>
          import("./ProvideLiquidity").then((m) => (
            <LiquidityContextProvider>
              <m.default />
            </LiquidityContextProvider>
          )),
      },
    ],
  },
  {
    path: paths.orders,
    element: () => import("./Orders").then((m) => <m.default />),
  },

  {
    element: () =>
      import("./NotFound").then(({ NotFound }) => (
        // 254px = (100px * 2) + (27px * 2) --> header height + padding-y
        <NotFound className="lg:min-h-[calc(100vh-254px)]" />
      )),
  },
];
